import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Dedicated admin login page. Only accepts admin/superuser accounts.
 * Main site login at /login is for registered users (listeners) only.
 */
export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout, isLoggedIn, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn && isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isLoggedIn, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const role = await login(username, password);
      if (role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        logout();
        setError('Admin access required. Use an admin or superuser account.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(
        message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')
          ? 'Cannot reach server. Check your connection or try again later.'
          : message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-100 to-slate-200 pt-14">
      <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-slate-800 rounded-2xl p-8 shadow-xl text-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-500/20 flex items-center justify-center">
              <span className="text-2xl" aria-hidden>🛡️</span>
            </div>
            <h1 className="text-2xl font-bold">Admin sign in</h1>
            <p className="text-slate-300 text-sm mt-1">Frequency Locus</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-400 text-red-100 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Admin username"
                required
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 text-slate-900 font-semibold py-2.5 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in to admin'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-slate-400 text-sm hover:text-white"
            >
              ← Back to site
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
